import {
    ChunkedRequestError,
    DEFAULT_MAX_API_RESULT_COUNT,
    cachedFetch,
    endpointWrapper,
} from './meta.api';
import { mergeEntities } from 'utils/entities';
import { transportFor } from 'utils/dataSources/transport';
import chunk from 'lodash/chunk';

/**
 * Resolve entities of one type against a single data source.
 *
 * `dataSource` is the API-side name (`google`, `pop_index`, `tiktok`,
 * `instagram`) — see `apiDataSource` in utils/entities. Returns a
 * `{ [id]: entity }` map, the same shape as the data_points sidecar.
 * `includePaths` adds ancestor chains for the breadcrumb; it doubles the
 * payload of a full page, so only that one caller asks for it.
 */
export const fetchEntities = ({
    dataSource,
    entityType,
    ids,
    names,
    minimal,
    countries,
    includePaths,
}) =>
    transportFor(dataSource)({
        domain: `${dataSource}/entities`,
        data: {
            entity_type: entityType,
            ids,
            names,
            minimal,
            countries,
            include_paths: includePaths,
        },
    });

/**
 * Which markets one data source has data for, per id: `{ [id]: [marketTextId] }`.
 *
 * A different question from `fetchEntities`, which answers from a source's
 * taxonomy. This reads its data store, so it answers for an id that source's
 * taxonomy does not carry at all — the id the no-data screen is asking about.
 * Only `categories` and `brands` have a market grain to report.
 */
export const fetchEntityPresence = ({ dataSource, entityType, ids }) =>
    cachedFetch(endpointWrapper)({
        domain: `${dataSource}/entities/presence`,
        data: { entity_type: entityType, ids },
    });

/**
 * Mirrors `_NON_MINIMAL_RESOLVERS` in the entities service; any other type 400s.
 */
const NON_MINIMAL_ENTITY_TYPES = new Set([
    'categories',
    'brands',
    'affinities',
]);

/**
 * Google data_points plus the entities its rows refer to.
 *
 * `paginator` cannot be reused: it flattens each chunk's response as an array,
 * and opting in to entities makes this endpoint return a `{data, entities}`
 * envelope. So chunk here and merge both halves.
 *
 * `minimal: false` costs a second request; the sidecar is minimal by design.
 * See the entities service README.
 */
export const fetchEntityData = async ({
    data,
    paginateBy,
    controller,
    minimal = true,
}) => {
    const ids = [...(data[paginateBy] || [])].sort();
    const chunks = chunk(ids, DEFAULT_MAX_API_RESULT_COUNT);

    const errors = [];
    const responses = await Promise.all(
        chunks.map((ids) =>
            cachedFetch(endpointWrapper)({
                domain: 'data_points',
                controller,
                data: {
                    ...data,
                    [paginateBy]: ids,
                    include_entities: true,
                },
            }).catch((error) => {
                errors.push({ error, ids });
                return undefined;
            }),
        ),
    );
    if (errors.length) throw new ChunkedRequestError(errors);

    const entities = mergeEntities(
        responses.map((response) => response?.entities),
    );

    if (!minimal && ids.length && NON_MINIMAL_ENTITY_TYPES.has(paginateBy)) {
        const full = await fetchEntities({
            dataSource: 'google',
            entityType: paginateBy,
            ids,
            minimal: false,
            countries: data.countries,
        });
        // Layered over the sidecar, so a row still resolves if the richer
        // lookup has no answer for it.
        entities[paginateBy] = { ...entities[paginateBy], ...(full || {}) };
    }

    return {
        rows: responses.flatMap((response) => response?.data || []),
        entities,
    };
};

/**
 * The same, for index-aligned pairs — an affinity paired with the category or
 * brand it was saved against.
 *
 * `pairedKeys` are chunked in lockstep with `paginateBy` so a chunk keeps its
 * pairs intact; chunking them independently would ask for the cross product.
 */
export const fetchEntityDataPairs = async ({
    data,
    paginateBy,
    pairedKeys,
    controller,
}) => {
    const size = data[paginateBy]?.length || 0;
    const indices = chunk(
        [...Array(size).keys()],
        DEFAULT_MAX_API_RESULT_COUNT,
    );

    const errors = [];
    const responses = await Promise.all(
        indices.map((idx) =>
            cachedFetch(endpointWrapper)({
                domain: 'data_points',
                controller,
                data: {
                    ...data,
                    [paginateBy]: idx.map((i) => data[paginateBy][i]),
                    ...pairedKeys.reduce((acc, key) => {
                        if (data[key]) acc[key] = idx.map((i) => data[key][i]);
                        return acc;
                    }, {}),
                    include_entities: true,
                },
            }).catch((error) => {
                errors.push({ error, ids: idx });
                return undefined;
            }),
        ),
    );
    if (errors.length) throw new ChunkedRequestError(errors);

    return {
        rows: responses.flatMap((response) => response?.data || []),
        entities: mergeEntities(
            responses.map((response) => response?.entities),
        ),
    };
};
